import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { AppStore } from '../../../store/store';
import { UserState } from '../../../store/user.slice';
import { apiService } from '../../../services/api.service';
import { ReconcileTransactionsFilters } from '../../../services/api.service-types';
import TableContent from '../../../components/table-content';
import TablePagination from '../../../components/table-pagination';
import TimeFilters from '../../../components/time-filters';
import AppSelect from '../../../components/form/base/app-select';
import Button from '../../../components/button';
import { ReactComponent as UserIcon } from '../../../assets/icons/user.svg';
import { ReactComponent as MerchantIcon } from '../../../assets/icons/merchant.svg';
import { ReactComponent as CardIcon } from '../../../assets/icons/processor-card.svg';
import { ReactComponent as ExcelIcon } from '../../../assets/icons/excel.svg';
import { LabelValue } from '../../../types/label-value.types';
import dateService from '../../../services/date.service';
import { formatAmount } from '../../../utils/number-utils';
import { errorService } from '../../../services/error.service';
import './reconciliation-page.scss';

interface Transaction {
    id: string;
    status: string;
    customerName: string;
    merchantName: string;
    processorName: string;
    amount: number;
    date: string;
    reconciliationStatus: 'OUTSTANDING' | 'RECONCILED' | null;
    transactionReferenceCode: string;
    customerCardType: string;
    customerLastFourCardDigits: string;
    processorTransactionId: string;
}

type ViewType = 'transactions' | 'uploadHistory';

interface ReconciliationLog {
    id: number;
    date: string;
    userId: string;
    userName: string;
    method: string;
    body: string;
}

const ReconciliationPage = () => {
    const navigate = useNavigate();
    const { user } = useSelector<AppStore, UserState>((state) => state.user);
    
    const [activeView, setActiveView] = useState<ViewType>('transactions');
    const [filters, setFilters] = useState<Partial<ReconcileTransactionsFilters>>({});
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [transactionsLoader, setTransactionsLoader] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalTransactions, setTotalTransactions] = useState(0);
    const [resetTrigger, setResetTrigger] = useState(false);
    const [midOptions, setMidOptions] = useState<LabelValue[]>([]);
    const [merchantOptions, setMerchantOptions] = useState<LabelValue[]>([]);
    const [processorsOptions, setProcessorsOptions] = useState<LabelValue[]>([]);
    const [selectedMID, setSelectedMID] = useState<string | null>(null);
    const [selectedMerchant, setSelectedMerchant] = useState<string | null>(null);
    const [selectedProcessor, setSelectedProcessor] = useState<string | null>(null);
    const [selectedReconciliationStatus, setSelectedReconciliationStatus] = useState<string | null>(null);
    
    const isMerchant = (user?.roles?.includes("merchant") || user?.roles?.includes("bas_merchant_view")) ?? false;
    
    // Upload History state
    const [logs, setLogs] = useState<ReconciliationLog[]>([]);
    const [logsLoader, setLogsLoader] = useState(false);
    const [logsCurrentPage, setLogsCurrentPage] = useState(1);
    const [logsTotalPages, setLogsTotalPages] = useState(1);
    const [logsTotalElements, setLogsTotalElements] = useState(0);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const reconciliationStatusOptions: LabelValue[] = [
        { label: 'Reset', value: '' },
        { label: 'OUTSTANDING', value: 'OUTSTANDING' },
        { label: 'RECONCILED', value: 'RECONCILED' },
    ];

    useEffect(() => {
        if (user?.id && isMerchant) {
            setSelectedMerchant(user.id);
        }
    }, [user, isMerchant]);

    useEffect(() => {
        if (selectedMID) {
            setFilters(prev => ({ ...prev, midId: selectedMID }));
        } else {
            setFilters(prev => {
                const { midId, ...rest } = prev;
                return rest;
            });
        }
    }, [selectedMID]);

    useEffect(() => {
        if (selectedMerchant) {
            setFilters(prev => ({ ...prev, merchantId: selectedMerchant }));
        } else {
            setFilters(prev => {
                const { merchantId, ...rest } = prev;
                return rest;
            });
        }
    }, [selectedMerchant]);

    useEffect(() => {
        if (selectedProcessor) {
            setFilters(prev => ({ ...prev, processorId: selectedProcessor }));
        } else {
            setFilters(prev => {
                const { processorId, ...rest } = prev;
                return rest;
            });
        }
    }, [selectedProcessor]);

    useEffect(() => {
        if (selectedReconciliationStatus) {
            setFilters(prev => ({ ...prev, reconciliationStatus: selectedReconciliationStatus as 'OUTSTANDING' | 'RECONCILED' }));
        } else {
            setFilters(prev => {
                const { reconciliationStatus, ...rest } = prev;
                return rest;
            });
        }
    }, [selectedReconciliationStatus]);

    useEffect(() => {
        setSelectedMID('');
        if (!isMerchant && selectedMerchant) {
            apiService.getMidsByMerchantAndProcessor({ merchantId: selectedMerchant, processorId: selectedProcessor })
                .then((response) => {
                    const transformedMids = response.data.map((mid: any) => ({
                        label: mid.midName || mid.midCode || 'Unknown',
                        value: mid.id,
                    }));
                    setMidOptions([{
                        label: 'Reset',
                        value: '',
                    }, ...transformedMids]);
                })
                .catch((error) => console.error('Error fetching mids:', error));
        }
    }, [selectedMerchant, selectedProcessor, isMerchant]);

    useEffect(() => {
        if (isMerchant && user?.id) {
            setSelectedMerchant(user.id);
            apiService.getMidsByMerchantAndProcessor({ merchantId: user?.id, processorId: selectedProcessor })
                .then((response) => {
                    const transformedMids = response.data.map((mid: any) => ({
                        label: mid.midName || mid.midCode || 'Unknown',
                        value: mid.id,
                    }));
                    setMidOptions([{
                        label: 'Reset',
                        value: '',
                    }, ...transformedMids]);
                })
                .catch((error) => console.error('Error fetching mids:', error));
        }
    }, [isMerchant, user?.id, selectedProcessor]);

    useEffect(() => {
        setTimeout(() => {
            if (!isMerchant) {
                apiService.getAllMerchants()
                    .then((response) => {
                        const transformedMerchants = response.data.map((merchant: any) => ({
                            label: merchant.lastName || merchant.firstName || 'Unknown',
                            value: merchant.id,
                        }));
                        setMerchantOptions([{
                            label: 'Reset',
                            value: '',
                        }, ...transformedMerchants]);
                    })
                    .catch((error) => console.error('Error fetching merchants:', error));
            }

            apiService.getAllProcessors()
                .then((response) => {
                    const transformedProcessors = response.data.map((processor: any) => ({
                        label: processor.processorName || processor.processorType || 'Unknown',
                        value: processor.id,
                    }));
                    setProcessorsOptions([{
                        label: 'Reset',
                        value: '',
                    }, ...transformedProcessors]);
                })
                .catch((error) => console.error('Error fetching processors:', error));

            // Load initial MID options
            if (!isMerchant) {
                apiService.getMidsByMerchantAndProcessor({ merchantId: null, processorId: null })
                    .then((response) => {
                        const transformedMids = response.data.map((mid: any) => ({
                            label: mid.midName || mid.midCode || 'Unknown',
                            value: mid.id,
                        }));
                        setMidOptions([{
                            label: 'Reset',
                            value: '',
                        }, ...transformedMids]);
                    })
                    .catch((error) => console.error('Error fetching mids:', error));
            }
        }, 500);
    }, [user, isMerchant]);

    useEffect(() => {
        if (!isMerchant) {
            setSelectedMerchant(null);
        }
        setSelectedProcessor(null);
        setSelectedMID(null);
        setSelectedReconciliationStatus(null);
    }, [resetTrigger, isMerchant]);

    useEffect(() => {
        if (activeView === 'transactions') {
            setTransactionsLoader(true);
            const pageInfo = {
                page: currentPage - 1,
                size: 10,
            };
            apiService.getReconcileTransactions(filters, pageInfo)
                .then((response) => {
                    const result = response.data;
                    if (result) {
                        setTransactions(result.content || []);
                        setTotalPages(result.totalPages || 1);
                        setTotalTransactions(result.totalElements || 0);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching reconcile transactions:', error);
                    setTransactions([]);
                })
                .finally(() => setTransactionsLoader(false));
        }
    }, [filters, currentPage, activeView]);

    useEffect(() => {
        if (activeView === 'uploadHistory') {
            setLogsLoader(true);
            const pageInfo = {
                page: logsCurrentPage - 1,
                size: 10,
            };
            apiService.getReconcileLogs(pageInfo)
                .then((response) => {
                    const result = response.data;
                    if (result) {
                        setLogs(result.content || []);
                        setLogsTotalPages(result.totalPages || 1);
                        setLogsTotalElements(result.totalElements || 0);
                    }
                })
                .catch((error) => {
                    console.error('Error fetching reconcile logs:', error);
                    setLogs([]);
                })
                .finally(() => setLogsLoader(false));
        }
    }, [logsCurrentPage, activeView]);

    const handleTimeFilters = (startDate?: string, endDate?: string) => {
        // Explicitly check if dates are empty strings (reset case)
        const isReset = (startDate === '' || startDate === undefined || startDate === null) && 
                       (endDate === '' || endDate === undefined || endDate === null);
        
        setFilters(prev => {
            // Always create a completely new object to ensure reference changes
            const { startDate: prevStartDate, endDate: prevEndDate, ...restFilters } = prev;
            
            // Build new filters object from scratch
            const updated: Partial<ReconcileTransactionsFilters> = {};
            
            // Copy over non-date filters
            if (restFilters.midId) updated.midId = restFilters.midId;
            if (restFilters.merchantId) updated.merchantId = restFilters.merchantId;
            if (restFilters.processorId) updated.processorId = restFilters.processorId;
            if (restFilters.reconciliationStatus) updated.reconciliationStatus = restFilters.reconciliationStatus;
            
            // Only add date fields if they have valid, non-empty values
            // When reset (empty strings), explicitly do NOT include startDate/endDate
            if (!isReset) {
                if (startDate && startDate !== '' && startDate.trim() !== '') {
                    updated.startDate = startDate.trim();
                }
                if (endDate && endDate !== '' && endDate.trim() !== '') {
                    updated.endDate = endDate.trim();
                }
            }
            // If reset, dates are already excluded (not added to updated object)
            
            return updated;
        });
        // Reset to first page when time filters change
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilters({});
        setResetTrigger(prev => !prev);
        setCurrentPage(1);
    };

    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
    };

    const handleLogsPageChange = (newPage: number) => {
        setLogsCurrentPage(newPage);
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            // Check if file is Excel format
            const validExtensions = ['.xlsx', '.xls', '.csv'];
            const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
            
            if (!validExtensions.includes(fileExtension)) {
                errorService.showError('Invalid File', 'Please select an Excel file (.xlsx, .xls, or .csv)');
                return;
            }
            
            setSelectedFile(file);
        }
    };

    const handleFileUpload = async () => {
        if (!selectedFile) {
            errorService.showError('No File Selected', 'Please select an Excel file to upload');
            return;
        }

        setUploadLoading(true);
        try {
            await apiService.uploadReconciliationExcel(selectedFile);
            errorService.showSuccess('Upload Successful', 'Reconciliation Excel file uploaded successfully');
            
            // Refresh logs after successful upload - go to first page to see the new upload
            setLogsCurrentPage(1);
            const pageInfo = {
                page: 0,
                size: 10,
            };
            apiService.getReconcileLogs(pageInfo)
                .then((response) => {
                    const result = response.data;
                    if (result) {
                        setLogs(result.content || []);
                        setLogsTotalPages(result.totalPages || 1);
                        setLogsTotalElements(result.totalElements || 0);
                    }
                })
                .catch((error) => {
                    console.error('Error refreshing logs:', error);
                });
        } catch (error: any) {
            errorService.showApiError(error, 'Upload Failed');
        } finally {
            // Clear upload component after API responds (success or error)
            setSelectedFile(null);
            const fileInput = document.getElementById('reconciliation-file-input') as HTMLInputElement;
            if (fileInput) {
                fileInput.value = '';
            }
            setUploadLoading(false);
        }
    };

    const handleReconciliationStatusChange = (transactionId: string, currentStatus: 'OUTSTANDING' | 'RECONCILED') => {
        const newStatus = currentStatus === 'OUTSTANDING' ? 'RECONCILED' : 'OUTSTANDING';
        apiService.changeReconcileStatus(transactionId, newStatus)
            .then(() => {
                // Refresh transactions after status change
                const pageInfo = {
                    page: currentPage - 1,
                    size: 10,
                };
                apiService.getReconcileTransactions(filters, pageInfo)
                    .then((response) => {
                        const result = response.data;
                        if (result) {
                            setTransactions(result.content || []);
                            setTotalPages(result.totalPages || 1);
                            setTotalTransactions(result.totalElements || 0);
                        }
                    })
                    .catch((error) => {
                        console.error('Error refreshing transactions:', error);
                    });
            })
            .catch((error) => {
                console.error('Error changing reconcile status:', error);
            });
    };

    const columns = [
        {
            Header: 'Trace ID',
            accessor: 'id',
            Cell: ({ value }: { value: string }) => (
                <div>
                    <span
                        className="id-text"
                        onClick={() => {
                            const url = `/app/transactions/transaction/${value}`;
                            window.open(url, '_blank');
                        }}
                    >{value}</span>
                </div>
            ),
        },
        {
            Header: 'Customer',
            accessor: 'customerName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <UserIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">
                        {row.original.customerCardType} xxxx xxxx xxxx {row.original.customerLastFourCardDigits}
                    </div>
                </div>
            ),
        },
        {
            Header: 'Merchant',
            accessor: 'merchantName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <MerchantIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">{row.original.transactionReferenceCode}</div>
                </div>
            ),
        },
        {
            Header: 'Channel',
            accessor: 'processorName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <div className="flex-row flex-gap-5 flex-align-start-center">
                        <CardIcon />
                        <span>{value}</span>
                    </div>
                    <div className="secondary-text accent-gradient-text">
                        {row.original.processorTransactionId}
                    </div>
                </div>
            ),
        },
        {
            Header: 'Value',
            accessor: 'amount',
            Cell: ({ value }: { value: number }) =>
                <span className="value-text accent-gradient-text">${formatAmount(value)}</span>
        },
        {
            Header: 'Status',
            accessor: 'status',
            Cell: ({ value }: { value: string }) => (
                <div className={`status ${value?.toLowerCase()} flex-row flex-align-center-center`}>
                    <span>{value}</span>
                </div>
            ),
        },
        {
            Header: 'Reconciliation Status',
            accessor: 'reconciliationStatus',
            Cell: ({ value, row }: { value: 'OUTSTANDING' | 'RECONCILED' | null; row: any }) => {
                // Treat null as OUTSTANDING
                const displayStatus = value === null ? 'OUTSTANDING' : value;
                const currentStatus = value === null ? 'OUTSTANDING' : value;
                
                return (
                    <div className="flex-row flex-align-center-center">
                        <AppSelect
                            theme="status"
                            options={[
                                { label: 'OUTSTANDING', value: 'OUTSTANDING' },
                                { label: 'RECONCILED', value: 'RECONCILED' },
                            ]}
                            placeholder={displayStatus}
                            value={displayStatus}
                            onChange={(newValue) => {
                                handleReconciliationStatusChange(row.original.id, currentStatus);
                            }}
                        />
                    </div>
                );
            },
        },
        {
            Header: 'Time',
            accessor: 'date',
            Cell: ({ value }: { value: string }) =>
                <div className="date-text">{dateService.formatDateForTable(value)}</div>
        },
    ];

    const logsColumns = [
        {
            Header: 'Date',
            accessor: 'date',
            Cell: ({ value }: { value: string }) =>
                <div className="date-text">{dateService.formatDateForTable(value)}</div>
        },
        {
            Header: 'User',
            accessor: 'userName',
            Cell: ({ value, row }: { value: string; row: any }) => (
                <div className="flex-column flex-gap-5">
                    <span>{value}</span>
                    <div className="secondary-text accent-gradient-text">{row.original.userId}</div>
                </div>
            ),
        },
        {
            Header: 'Method',
            accessor: 'method',
        },
        {
            Header: 'Body',
            accessor: 'body',
            Cell: ({ value }: { value: string }) => (
                <div className="body-text" title={value}>
                    {value && value.length > 100 ? `${value.substring(0, 100)}...` : value}
                </div>
            ),
        },
    ];

    return (
        <div className="reconciliation-page page-content">
            <div className="page-header flex-row flex-gap-20 flex-align-start-center">
                <div className='page-title'>
                    Reconciliation
                </div>
            </div>
            
            <div className="view-toggle-container">
                <div className="view-toggle">
                    <div
                        className={`toggle-button ${activeView === 'transactions' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveView('transactions');
                            setCurrentPage(1);
                        }}
                    >
                        <span>Transactions</span>
                    </div>
                    <div
                        className={`toggle-button ${activeView === 'uploadHistory' ? 'active' : ''}`}
                        onClick={() => {
                            setActiveView('uploadHistory');
                            setLogsCurrentPage(1);
                        }}
                    >
                        <span>Upload History</span>
                    </div>
                </div>
            </div>

            {activeView === 'transactions' ? (
                <>
                    <div className="reconciliation-filters-container">
                        <TimeFilters
                            handleFilters={handleTimeFilters}
                            transactionsNo={totalTransactions}
                            resetTrigger={resetTrigger}
                        />
                        
                        <div className="reconciliation-filters flex-row flex-gap-20 flex-wrap">
                            {!isMerchant && (
                                <div className="filter-item">
                                    <AppSelect
                                        theme="filter"
                                        placeholder="Select Merchant"
                                        options={merchantOptions}
                                        value={selectedMerchant || ''}
                                        onChange={(value) => setSelectedMerchant(value || null)}
                                    />
                                </div>
                            )}
                            <div className="filter-item">
                                <AppSelect
                                    theme="filter"
                                    placeholder="Select Processor"
                                    options={processorsOptions}
                                    value={selectedProcessor || ''}
                                    onChange={(value) => setSelectedProcessor(value || null)}
                                />
                            </div>
                            <div className="filter-item">
                                <AppSelect
                                    theme="filter"
                                    placeholder="Select MID"
                                    options={midOptions}
                                    value={selectedMID || ''}
                                    onChange={(value) => setSelectedMID(value || null)}
                                />
                            </div>
                            <div className="filter-item">
                                <AppSelect
                                    theme="filter"
                                    placeholder="Reconciliation Status"
                                    options={reconciliationStatusOptions}
                                    value={selectedReconciliationStatus || ''}
                                    onChange={(value) => setSelectedReconciliationStatus(value || null)}
                                />
                            </div>
                        </div>
                    </div>
                    
                    <Button theme='accent' label='Clear Filters' onClick={resetFilters} />
                    
                    <TablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                    
                    <div className='inner-page flex-column flex-gap-30 width-100'>
                        <TableContent
                            columns={columns}
                            data={transactions}
                            isLoading={transactionsLoader}
                        />
                    </div>
                    
                    <TablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                    />
                </>
            ) : (
                <>
                    <div className="upload-section">
                        <div className="upload-header">
                            <div className="upload-icon-wrapper">
                                <ExcelIcon className="upload-icon" />
                            </div>
                            <div className="upload-header-text">
                                <h3 className="upload-title">Upload Reconciliation File</h3>
                                <p className="upload-description">Select an Excel file (.xlsx, .xls, or .csv) to upload reconciliation data</p>
                            </div>
                        </div>
                        <div className="upload-container">
                            <div className="file-input-wrapper">
                                <input
                                    id="reconciliation-file-input"
                                    type="file"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={handleFileSelect}
                                    className="file-input"
                                    disabled={uploadLoading}
                                />
                                <label htmlFor="reconciliation-file-input" className={`file-input-label ${selectedFile ? 'has-file' : ''} ${uploadLoading ? 'loading' : ''}`}>
                                    {selectedFile ? (
                                        <div className="file-selected-content">
                                            <ExcelIcon className="file-selected-icon" />
                                            <div className="file-selected-info">
                                                <span className="file-name">{selectedFile.name}</span>
                                                <span className="file-size">{(selectedFile.size / 1024).toFixed(2)} KB</span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="file-dropzone-content">
                                            <ExcelIcon className="file-dropzone-icon" />
                                            <span className="file-placeholder">Click to choose file or drag and drop</span>
                                            <span className="file-hint">Excel files only (.xlsx, .xls, .csv)</span>
                                        </div>
                                    )}
                                </label>
                            </div>
                            <div className="upload-actions">
                                <button
                                    className={`upload-button ${!selectedFile || uploadLoading ? 'disabled' : ''}`}
                                    onClick={handleFileUpload}
                                    disabled={!selectedFile || uploadLoading}
                                >
                                    <span>{uploadLoading ? 'Uploading...' : 'Upload'}</span>
                                </button>
                                {selectedFile && (
                                    <button
                                        className="cancel-button"
                                        onClick={() => {
                                            setSelectedFile(null);
                                            const fileInput = document.getElementById('reconciliation-file-input') as HTMLInputElement;
                                            if (fileInput) {
                                                fileInput.value = '';
                                            }
                                        }}
                                        disabled={uploadLoading}
                                    >
                                        <span>Cancel</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    <TablePagination
                        currentPage={logsCurrentPage}
                        totalPages={logsTotalPages}
                        onPageChange={handleLogsPageChange}
                    />
                    
                    <div className='inner-page flex-column flex-gap-30 width-100'>
                        <TableContent
                            columns={logsColumns}
                            data={logs}
                            isLoading={logsLoader}
                        />
                    </div>
                    
                    <TablePagination
                        currentPage={logsCurrentPage}
                        totalPages={logsTotalPages}
                        onPageChange={handleLogsPageChange}
                    />
                </>
            )}
        </div>
    );
};

export default ReconciliationPage;
